import type { ChannelSlice } from '../ChannelSlice';
import type { ColumnOptionsSlice } from '../ColumnOptionsSlice';
import type { OrdersWidgetAdapter } from '../OrdersWidgetAdapter';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { signal, computed } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import type { ColumnTemplate } from '@thinkalpha/platform-ws-client/contracts/column-template.js';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { Observable } from 'rxjs';
import type { TableStatus } from 'src/components/table-view/model';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { enableTableColumnFilters } from 'src/lib/featureFlags/flags/enableTableColumnFilters';
import { riskWidgetsShouldShowNewSymbolPicker } from 'src/lib/featureFlags/flags/riskWidgetsShouldShowNewSymbolPicker';
import { instrumentToString, parseInstrument } from 'src/lib/instrumentUtils';
import type { ColumnFiltersModel } from 'src/models/ColumnFiltersModel';
import type { ColumnFiltersEventBus, TableColumnFilters } from 'src/models/ColumnFiltersModel/tableColumnFilters';
import { templateColumnFilters } from 'src/models/ColumnFiltersModel/templateColumnFilters';
import type {
    ColumnFilterTemplateColumns,
    TemplateColumnFilters,
} from 'src/models/ColumnFiltersModel/templateColumnFilters';
import type { OrdersWidgetModel } from 'src/models/OrdersWidgetModel';
import type { TableConstructionProgress } from 'src/models/TableModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal, FormattableGridSettingsSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

@injectable()
export class OrdersWidgetModelImpl extends ReactiveInjectable implements OrdersWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #stickyFilterSlice: StickyFilterSlice;
    #columnOptionsSlice: ColumnOptionsSlice;

    #instrument = signal<Instrument>(parseInstrument(''));
    #isManualReviewSoundAlertEnabled = signal<boolean>(true);
    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity', 'options']);
    #accountIds: Signal<string[]> = signal([]);
    #platforms: Signal<string[]> = signal([]);
    #isAccountSelectionValid: Signal<boolean> = signal(true);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);
    #columnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #filter = signal<string>('');
    #filterIsSticky = signal<boolean>(false);
    #tableRowCount: Signal<number> = signal(0);
    #tableRowCountObservable: Signal<Observable<number> | undefined> = signal(undefined);
    #tableTotalRowCount: Signal<number | null> = signal(null);
    #connectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        failureCount: 0,
        progress: undefined,
    });
    #refetchCallback: (() => void) | null = null;

    headerElements!: WidgetHeaderElement[];

    eventBus!: OrdersWidgetModel['eventBus'];

    readonly #columnFilters: TemplateColumnFilters;

    get columnFilterState(): ColumnFiltersModel {
        return this.#columnFilters.state;
    }

    get columnFilters(): TableColumnFilters {
        return this.#columnFilters.views;
    }

    setColumnFilterColumns(columns: ColumnFilterTemplateColumns): void {
        this.#columnFilters.setColumns(columns);
    }

    constructor(
        @inject('OrdersWidgetAdapter') private adapter: OrdersWidgetAdapter,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('ColumnOptionsSlice') columnOptionsSlice: ColumnOptionsSlice,
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#stickyFilterSlice = stickyFilterSlice;
        this.#columnOptionsSlice = columnOptionsSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        // Session-only column filters (ADR 0014); kept out of adapter persistence.
        this.#columnFilters = templateColumnFilters(featureFlagService.getSignal(enableTableColumnFilters));
        this.disposableStack.use(this.#columnFilters.state);

        this.eventBus = this.#buildEventBus();
    }

    get isManualReviewSoundAlertEnabled(): ReadonlySignal<boolean> {
        return this.#isManualReviewSoundAlertEnabled as ReadonlySignal<boolean>;
    }

    setIsManualReviewSoundAlertEnabled(isManualReviewSoundAlertEnabled: boolean) {
        this.adapter.persist({ isManualReviewSoundAlertEnabled });
        this.#isManualReviewSoundAlertEnabled.value = isManualReviewSoundAlertEnabled;
    }

    get instrument(): ReadonlySignal<Instrument> {
        return this.#instrument as ReadonlySignal<Instrument>;
    }

    setInstrument(instrument: Instrument) {
        this.adapter.persist({ instrument });
        this.#instrument.value = instrument;
    }

    selectOrder(symbol?: string, accountId?: string) {
        this.adapter.selectOrder(symbol, accountId);
    }

    #buildEventBus(): OrdersWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.setInstrument(parseInstrument(payload.instrument));
                })
                .addEvent('widget::updateSelectedAccountIds', (payload) => {
                    this.setAccountIds(payload.accountIds);
                })
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    this.setAccountIds([payload.accountId]);
                })
                .addEvent('widget::setIsAccountSelectionValid', (payload) => {
                    this.#isAccountSelectionValid.value = payload.isValid;
                })
                .addEvent('widget::updateSelectedPlatforms', (payload) => {
                    this.adapter.persist({ platforms: payload.platformIds });
                    this.#platforms.value = payload.platformIds;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.setColumnTemplate(payload.columnTemplate);
                })
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.setColumnTemplate(payload.templateId);
                })
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                .addEvent('widget::toggleManualReviewIsAlertsEnabled', ({ isEnabled }) =>
                    this.setIsManualReviewSoundAlertEnabled(isEnabled),
                )
                .addEvent('widget::openManualReviewStandalone', () => {
                    this.adapter.openManualReviewStandalone();
                })
                // Signals
                .addSignal('widget::instrument', () => computed(() => instrumentToString(this.#instrument.value)))
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::selectedAccountIds', () =>
                    computed(() => this.#accountIds.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::isAccountSelectionValid', () => this.#isAccountSelectionValid)
                .addSignal('widget::selectedPlatforms', () =>
                    computed(() => this.#platforms.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal(
                    'widget::formattableGridSettings',
                    () =>
                        computed(() => ({
                            settings: null,
                            defaults: null,
                        })) satisfies FormattableGridSettingsSignal[keyof FormattableGridSettingsSignal],
                )
                .addSignal('widget::selectedColumnTemplate', () => this.#columnTemplate)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::tableRowCountObservable', () => this.#tableRowCountObservable)
                .addSignal('widget::tableTotalRowCount', () => this.#tableTotalRowCount)
                .addSignal('widget::isManualReviewAlertsEnabled', () => this.isManualReviewSoundAlertEnabled)
                .addSignal('widget::standalonePageUrl', () =>
                    computed(() => {
                        if (this.widgetType !== 'manual-review') return null;

                        const params = new URLSearchParams();

                        const accounts = this.#accountIds.value;
                        if (accounts.length > 0) params.set('accounts', accounts.join(','));

                        if (!this.#isManualReviewSoundAlertEnabled.value) params.set('alert', 'false');

                        const filter = this.#stickyFilterSlice.eventBus.getSignal('widget::stickyFilterValue').value;
                        if (filter) params.set('filter', filter);

                        const isSticky =
                            this.#stickyFilterSlice.eventBus.getSignal('widget::isStickyFilterSticky').value;
                        if (isSticky) params.set('sticky', 'true');

                        const template = this.#columnTemplate.value;
                        if (typeof template === 'string') {
                            params.set('template', template);
                        } else if (
                            template &&
                            typeof template === 'object' &&
                            'id' in template &&
                            typeof template.id === 'string'
                        ) {
                            params.set('template', template.id);
                        }

                        const query = params.toString();
                        return `/manual-review${query ? `?${query}` : ''}`;
                    }),
                )
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchCallback?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#stickyFilterSlice.eventBus)
            .merge<ColumnFiltersEventBus>(this.#columnFilters.views.eventBus);
    }

    init(tabId?: string) {
        // Always initialize slices — use a generated UUID for standalone mode so signals are available
        const effectiveTabId = tabId ?? crypto.randomUUID();
        this.#channelSlice.init(effectiveTabId);
        this.#widgetTabSlice.init(effectiveTabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        if (!tabId) {
            (this.#widgetTabSlice.eventBus.getSignal('widget::name') as Signal<string>).value =
                this.widgetType === 'orders' ? t('widgets::orders::name') : t('widgets::manual-review::name');
        }

        // Initialize StickyFilterSlice and ColumnOptionsSlice with the shared adapter
        this.#stickyFilterSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);

        this.#columnOptionsSlice.init(this.adapter);
        this.disposableStack.use(this.#columnOptionsSlice);

        // Sync current adapter state into signals
        this.#syncFromAdapter();

        // Build headerElements based on widget type
        const isOrdersWidget = this.widgetType === 'orders';
        const isManualReviewWidget = this.widgetType === 'manual-review';

        const shouldShowSymbolPickerFlagValue = this.featureFlagService.getValue(riskWidgetsShouldShowNewSymbolPicker);
        const shouldShowSymbolPicker = isOrdersWidget && shouldShowSymbolPickerFlagValue;

        const baseElements = tabId ? COMMON_HEADER_ELEMENTS : STANDALONE_COMMON_HEADER_ELEMENTS;
        this.headerElements = makeHeaderElements<OrdersWidgetModel['eventBus']>([
            ...baseElements,
            // Left side (before spacer at position 50)
            // Symbol picker comes right after name, before sticky filter
            ...(shouldShowSymbolPicker
                ? [
                      {
                          type: 'symbolPicker',
                          priority: 150,
                          position: 25,
                      } as const,
                  ]
                : []),
            { type: 'stickyFilter', position: 26, priority: 160 },
            { type: 'columnFilters', position: 26.5, priority: 165 },
            // Right side (after spacer at position 50)
            {
                type: 'connectionStatus',
                position: 76,
                priority: 12,
                overflowPosition: 5,
            },
            { type: 'accounts', priority: 5, position: 60, selectType: 'multi', allAccountsLabel: 'all-accounts' },
            // Only show platforms and column template menu for orders widget (not manual-review)
            ...(isOrdersWidget
                ? [
                      { type: 'platforms', position: 65, priority: 6 } as const,
                      { type: 'columnTemplateMenu', position: 70, priority: 9, columnTemplateKey: 'Order' } as const,
                  ]
                : [{ type: 'openManualReviewStandalone', position: 78, priority: 9 } as const]),
            { type: 'tableRowCount', position: 75, priority: 11 },
            ...(isManualReviewWidget
                ? [
                      { type: 'manualReviewAlertsSwitch', position: 55, priority: 3 } as const,
                      { type: 'columnTemplateMenu', position: 70, priority: 9, columnTemplateKey: 'Order' } as const,
                  ]
                : []),
        ]);
    }

    #syncFromAdapter() {
        const state = this.adapter.getState();
        this.#instrument.value = state.instrument;
        this.#isManualReviewSoundAlertEnabled.value = state.isManualReviewSoundAlertEnabled;
        this.#accountIds.value = state.accountIds;
        this.#platforms.value = state.platforms;
        this.#columnTemplate.value = state.columnTemplate;
    }

    get widgetType() {
        return this.adapter.getState().widgetType;
    }

    // Account selection
    get accountIds(): ReadonlySignal<string[]> {
        return this.#accountIds;
    }

    setAccountIds(accountIds: string[]) {
        this.adapter.persist({ accountIds });
        this.#accountIds.value = accountIds;
    }

    // Filter
    get filter(): ReadonlySignal<string> {
        return this.#filter;
    }

    updateFilter(filter: string) {
        this.#stickyFilterSlice.eventBus.emit('widget::updateStickyFilter', { filter });
        this.#filter.value = filter;
    }

    get filterIsSticky(): ReadonlySignal<boolean> {
        return this.#filterIsSticky;
    }

    toggleStickyFilter() {
        this.#stickyFilterSlice.eventBus.emit('widget::toggleStickyFilter', undefined);
        this.#filterIsSticky.value = this.#stickyFilterSlice.eventBus.getSignal('widget::isStickyFilterSticky').value;
    }

    // Column template
    get columnTemplate(): ReadonlySignal<ColumnTemplate | string | null> {
        return this.#columnTemplate;
    }

    setColumnTemplate(columnTemplate: ColumnTemplate | string | null) {
        this.adapter.persist({ columnTemplate });
        this.#columnTemplate.value = columnTemplate;
    }

    setTableRowCount(count: number) {
        this.#tableRowCount.value = count;
    }

    setTableRowCountObservable(observable: Observable<number> | undefined) {
        this.#tableRowCountObservable.value = observable;
    }

    setTableTotalRowCount(count: number | null) {
        this.#tableTotalRowCount.value = count;
    }

    updateTableConnectionStatus(connectionStatus: {
        status: TableStatus;
        progress: TableConstructionProgress;
        failureCount: number;
    }): void {
        this.#connectionStatus.value = connectionStatus;
    }

    setRefetchCallback(fn: (() => void) | null): void {
        this.#refetchCallback = fn;
    }
}
